import React, {useState, useEffect, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {companiesApi} from '@/services/api';
import {Company, CompanyVolumeStatsData} from '@/types/client';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    Brush
} from 'recharts';
import {
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    IconButton,
    Box,
    Typography,
    Tooltip as MuiTooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { PageFrame, PageHeader, Surface, Button, Note, Placeholder, FilterAsyncSelect, StatGrid, loadingStats } from '@/components/ui';
import type { FilterOption } from '@/components/ui';
import { chartInk, chartSeries } from '@/theme/chartColors';
import './VolumePage.css';

// Utility to format numbers compactly
const formatNumber = (num: number | undefined): string => {
    if (num == null) return 'N/A';
    if (num >= 10_000_000) {
        // 1 Crore = 10,000,000
        return (num / 10_000_000).toFixed(1).replace(/\.0$/, '') + 'Cr';
    }
    if (num >= 100_000) {
        // 1 Lakh = 100,000
        return (num / 100_000).toFixed(1).replace(/\.0$/, '') + 'L';
    }
    if (num >= 1_000) {
        return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toLocaleString();
};

interface CompanyWithVolumeData extends Company {
    latest_median_volume?: number;
    last_updated?: string;
}

interface ChartDataPoint {
    date: string;
    volume: number;
    median_volume_21d: number;
}

function getLastNMonthsDate(months: number): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - months, today.getDate());
}

const CompanySearch: React.FC<{
    loadOptions: (input: string) => Promise<FilterOption[]>;
    value: FilterOption | null;
    onChange: (value: FilterOption | null) => void;
}> = ({loadOptions, value, onChange}) => (
    <Surface padded>
        <Box sx={{maxWidth: 520}}>
            <FilterAsyncSelect
                label="Search for a company…"
                value={value}
                onChange={onChange}
                loadOptions={loadOptions}
            />
        </Box>
    </Surface>
);

const EmptyState: React.FC = () => (
    <Surface>
        <Placeholder
            message="No company selected"
            hint="Search for a company above to view its volume data and trend."
        />
    </Surface>
);

const CompanyInfo: React.FC<{
    company: Company;
    volumeData: CompanyVolumeStatsData | null;
    watchlist: CompanyWithVolumeData[];
    onAddToWatchlist: () => void;
}> = ({company, volumeData, watchlist, onAddToWatchlist}) => {
    return (
        <Surface
            title={company.name}
            meta={company.symbol}
            actions={
                <Button
                    size="sm"
                    onClick={onAddToWatchlist}
                    disabled={watchlist.some(c => c.id === company.id) || !volumeData}
                >
                    Add to watchlist
                </Button>
            }
            padded
        >
                {volumeData ? (
                    <StatGrid
                        items={[{
                            label: '21-Day Median',
                            value: formatNumber(volumeData.latest_median_volume),
                            title: volumeData.latest_median_volume?.toLocaleString()
                                ?? formatNumber(volumeData.latest_median_volume),
                        }]}
                    />
                ) : (
                    <StatGrid items={loadingStats(['21-Day Median'])} />
                )}
        </Surface>
    );
};

interface CustomTickProps {
    x?: number;
    y?: number;
    payload?: {
        value: string | number;
    };
    index?: number;
    data: ChartDataPoint[];
}

const CustomXAxisTick: React.FC<CustomTickProps> = (props) => {
    const {x, y, payload, index, data} = props;
    const [hover, setHover] = useState(false);

    if (!payload || payload.value == null || !data) return null;

    const date = new Date(payload.value as string);
    const monthDay = date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
    let year = '';

    const actualIndex = data.findIndex((item: ChartDataPoint) => {
        const itemDate = item.date;
        return new Date(itemDate).getTime() === date.getTime();
    });

    if (index === 0) {
        year = date.getFullYear().toString();
    } else if (actualIndex > 0) {
        const prevItem = data[actualIndex - 1];
        const prevDate = new Date(prevItem.date);

        if (date.getFullYear() !== prevDate.getFullYear()) {
            year = date.getFullYear().toString();
        }
    }

    const fullDate = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <g
            transform={`translate(${x},${y})`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <text
                x={0}
                y={0}
                dy={16}
                textAnchor="middle"
                fill={hover ? chartInk.strong : chartInk.axis}
                fontSize={12}
                style={{cursor: "pointer"}}
            >
                {monthDay}{year ? `, ${year}` : ""}
            </text>
            {hover && (
                <text
                    x={0}
                    y={-20}
                    textAnchor="middle"
                    fill={chartInk.strong}
                    fontSize={11}
                    fontWeight={500}
                >
                    {fullDate}
                </text>
            )}
        </g>
    );
};

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        name?: string;
        value?: number | string | null;
        color?: string;
    }>;
    label?: string;
}

interface VolumeChartProps {
    data?: CompanyVolumeStatsData | null;
    loading: boolean;
}

const VolumeChart: React.FC<VolumeChartProps> = React.memo(
    ({data, loading}) => {
        const lastMonthDate = getLastNMonthsDate(1);

        const CustomTooltip: React.FC<CustomTooltipProps> = ({active, payload, label}) => {
            if (active && payload && payload.length) {
                const dailyVolumeData = payload.find((item: any) => item.name === 'Daily Volume');
                const medianVolumeData = payload.find((item: any) => item.name === '21-Day Median Volume');

                const dailyVolume = (dailyVolumeData?.value as number) ?? 0;
                const medianVolume = (medianVolumeData?.value as number) ?? 0;

                return (
                    <div className="custom-tooltip">
                        <p>{String(label ?? '')}</p>
                        <p>Daily Volume: {formatNumber(dailyVolume)}</p>
                        <p>21-Day Median: {formatNumber(medianVolume)}</p>
                    </div>
                );
            }
            return null;
        };

        return (
            <Surface title="Historical Volume Trend" padded>
                    <div className="chart-container">
                        {loading ? (
                            <Placeholder kind="loading" message="Loading chart data…"/>
                        ) : !data || data.timeseries.length === 0 ? (
                            <Placeholder message="No volume data for this company."/>
                        ) : (
                            <ResponsiveContainer width="100%" height={400}>
                                <LineChart data={data.timeseries} margin={{top: 5, right: 30, left: 20, bottom: 5}}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartInk.grid}/>
                                    <XAxis
                                        dataKey="date"
                                        tick={<CustomXAxisTick data={data.timeseries}/>}
                                        minTickGap={30}
                                        stroke={chartInk.axis}
                                        fontSize={12}
                                    />
                                    <YAxis
                                        label={{value: 'INR Volume', angle: -90, position: 'insideLeft', fontWeight: 600}}
                                        tickFormatter={formatNumber}
                                        stroke={chartInk.axis}
                                        fontSize={12}
                                    />
                                    <RechartsTooltip content={<CustomTooltip/>}/>
                                    <Legend/>
                                    <Line
                                        type="monotone"
                                        dataKey="volume"
                                        stroke={chartSeries[0]}
                                        name="Daily Volume"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="median_volume_21d"
                                        stroke={chartSeries[1]}
                                        name="21-Day Median Volume"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                    <Brush
                                        dataKey="date"
                                        height={25}
                                        stroke={chartSeries[0]}
                                        travellerWidth={8}
                                        startIndex={Math.max(
                                            0,
                                            data.timeseries.findIndex((d) => new Date(d.date) >= lastMonthDate)
                                        )}
                                        endIndex={data.timeseries.length - 1}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
            </Surface>
        );
    }
);
VolumeChart.displayName = 'VolumeChart';

VolumeChart.propTypes = {
    data: PropTypes.oneOfType([
        PropTypes.shape({
            latest_median_volume: PropTypes.number,
            timeseries: PropTypes.arrayOf(
                PropTypes.shape({
                    date: PropTypes.string.isRequired,
                    volume: PropTypes.number.isRequired,
                    median_volume_21d: PropTypes.number.isRequired,
                }).isRequired
            ).isRequired,
        }),
        PropTypes.oneOf([null])
    ]),
    loading: PropTypes.bool.isRequired,
};

// Sub-component for Watchlist Item (compact)
const WatchlistItem: React.FC<{
    company: CompanyWithVolumeData;
    onRemove: (id: number) => void;
    onSelect: (company: Company) => void;
}> = ({company, onRemove, onSelect}) => {
    return (
        <ListItem
            className="watchlist-item"
            sx={{
                mb: 1,
                borderRadius: 1,
                px: 1,
                py: 0.5,
                cursor: "pointer",
            }}
            secondaryAction={
                <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(company.id);
                    }}
                    size="small"
                    sx={{color: 'error.main'}}
                >
                    <DeleteIcon fontSize="small"/>
                </IconButton>
            }
            disablePadding
        >
            <ListItemButton onClick={() => onSelect(company)}>
                <ListItemText
                    primary={
                        <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                            {company.name}
                        </Typography>
                    }
                    secondary={
                        <>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {company.symbol}
                            </Typography>
                            <Box display="flex" gap={2} alignItems="center" mt={0.5}>
                                <MuiTooltip title={`21-Day Median Volume: ${formatNumber(company.latest_median_volume)}`} arrow>
                                    <Box component="span" sx={{display: 'flex', flexDirection: 'column', minWidth: 96}}>
                                        <Typography variant="caption" color="text.secondary">
                                            21DMV
                                        </Typography>
                                        <MuiTooltip title={company.latest_median_volume?.toLocaleString() ?? formatNumber(company.latest_median_volume)}>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                {formatNumber(company.latest_median_volume)}
                                            </Typography>
                                        </MuiTooltip>
                                    </Box>
                                </MuiTooltip>
                            </Box>
                            {company.last_updated && (
                                <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', mt: 0, mb: -1}}>
                                    <Typography variant="caption" color="text.disabled">
                                        {new Date(company.last_updated).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    }
                    disableTypography
                />
            </ListItemButton>
        </ListItem>
    );
};

// Sub-component for Watchlist
const Watchlist: React.FC<{
    watchlist: CompanyWithVolumeData[];
    onRemove: (id: number) => void;
    onSelect: (company: Company) => void;
}> = ({watchlist, onRemove, onSelect}) => (
    <div className="watchlist-section">
        <Surface title="Watchlist" padded>
                {watchlist.length === 0 ? (
                    <Placeholder message="No companies in watchlist" hint="Add one from the card on the left."/>
                ) : (
                    <List dense sx={{maxHeight: 400, overflow: 'auto', px: 0}}>
                        {watchlist.map(company => (
                            <WatchlistItem
                                key={company.id}
                                company={company}
                                onRemove={onRemove}
                                onSelect={onSelect}
                            />
                        ))}
                    </List>
                )}
        </Surface>
    </div>
);

const VolumePage: React.FC = () => {
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [volumeData, setVolumeData] = useState<CompanyVolumeStatsData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedCompanyOption, setSelectedCompanyOption] = useState<FilterOption | null>(null);
    const [watchlist, setWatchlist] = useState<CompanyWithVolumeData[]>([]);

    // FilterAsyncSelect only carries {value, label} — keep the full Company objects
    // behind the scenes so a selection can still hand one to the rest of the page.
    const companiesByValue = useRef<Map<string, Company>>(new Map());

    const loadCompanyOptions = useCallback(async (searchTerm: string): Promise<FilterOption[]> => {
        try {
            const response = await companiesApi.getCompanies({
                search: searchTerm || "",
                page_size: 100,
            });
            response.results.forEach((company: Company) => {
                companiesByValue.current.set(String(company.id), company);
            });
            return response.results.map((company: Company) => ({
                value: String(company.id),
                label: `${company.name} (${company.symbol})`,
            }));
        } catch (err) {
            console.error("Failed to fetch companies:", err);
            setError("Failed to fetch companies.");
            return [];
        }
    }, []);

    const handleCompanyChange = (option: FilterOption | null) => {
        setSelectedCompanyOption(option);
        setSelectedCompany(option ? companiesByValue.current.get(option.value) ?? null : null);
    };

    const fetchVolumeData = useCallback(async () => {
        if (!selectedCompany) {
            setVolumeData(null);
            return;
        }

        setLoading(true);
        setError(null);
        setVolumeData(null);

        try {
            const volumeStats = await companiesApi.getCompanyVolumeStats(selectedCompany.id);
            setVolumeData(volumeStats);
        } catch (err) {
            setError('Failed to fetch volume data.');
            setVolumeData(null);
            console.error('Failed to fetch volume data:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedCompany]);

    useEffect(() => {
        fetchVolumeData();
    }, [fetchVolumeData]);

    const addToWatchlist = () => {
        if (selectedCompany && !watchlist.some(c => c.id === selectedCompany.id) && volumeData) {
            const companyWithVolumeData: CompanyWithVolumeData = {
                ...selectedCompany,
                latest_median_volume: volumeData.latest_median_volume,
                last_updated: new Date().toISOString(),
            };
            setWatchlist(prev => [...prev, companyWithVolumeData]);
        }
    };

    const removeFromWatchlist = (id: number) => {
        setWatchlist(prev => prev.filter(c => c.id !== id));
    };

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Volume Monitor"
                    subtitle="Daily traded volume against its 21-day median, for one company at a time."
                />
            }
        >
            <CompanySearch
                loadOptions={loadCompanyOptions}
                value={selectedCompanyOption}
                onChange={handleCompanyChange}
            />

            {error && <Note tone="error">{error}</Note>}

            <div className="main-content">
                <div className="chart-section">
                    {selectedCompany ? (
                        <>
                            <CompanyInfo
                                company={selectedCompany}
                                volumeData={volumeData}
                                watchlist={watchlist}
                                onAddToWatchlist={addToWatchlist}
                            />
                            {!error && <VolumeChart data={volumeData} loading={loading}/>}
                        </>
                    ) : (
                        <EmptyState/>
                    )}
                </div>

                <Watchlist
                    watchlist={watchlist}
                    onRemove={removeFromWatchlist}
                    onSelect={(company) => setSelectedCompany(company)}
                />
            </div>
        </PageFrame>
    );
};

export default VolumePage;